import React from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {debounce} from 'throttle-debounce';
import AsyncSelect from 'react-select/lib/Async';
import exportMessages from '../../intl/common/exportMessages';
import validationMessages from '../../intl/common/validationMessages';
import ValidFormattedMessage from './ValidFormattedMessage';
import {DEFAULT_DEBOUNCE_TIMEOUT} from '../../constants/validationConstants';

const ErrorDiv = styled.label`
    display: inherit;
`;

const mapDealerToSelectionOption = (dealer) => {
    return {
        ...dealer,
        value: dealer.dealerNumber,
        label: `${dealer.dealerNumber} / ${dealer.dealerName}`
    };
};

const SelectDealer = props => {
    const {
        label, 
        placeholder, 
        noOptionsMessage, 
        handleDealerSelection, 
        handleDealerSearch,
        intl: {formatMessage}, 
        dealerNumber,
        dealerName,
        onDealerChange,
        searchAseDealers,
        isRequired,
        isDisabled,
        input,
        meta
    } = props;

    // Handle redux-form integration
    const isReduxFormIntegrated = input && meta;
    const hasError = isReduxFormIntegrated && meta.touched && meta.error;

    const handleDealerSearchInternal = (inputValue, callback) => {
        if (!inputValue || inputValue.trim() === '') {
            callback([]);
            return;
        }

        // Use the searchAseDealers action if provided (redux-form mode)
        if (searchAseDealers) {
            searchAseDealers(inputValue.trim())
                .then(dealers => {
                    const options = [...dealers]
                        .sort((dealer1, dealer2) => dealer1.dealerName.localeCompare(dealer2.dealerName))
                        .map(mapDealerToSelectionOption);
                    callback(options);
                })
                .catch(error => {
                    console.error('Dealer search failed:', error);
                    callback([]);
                });
        } else if (handleDealerSearch) {
            // Fallback to handleDealerSearch callback (legacy mode)
            handleDealerSearch(inputValue, callback);
        }
    };

    const handleSelectionChange = (selectedOption) => {
        
        if (isReduxFormIntegrated && input) {
            // Update redux-form field value
            const value = selectedOption ? selectedOption.value : '';
            input.onChange(value);
        }

        // Legacy mode
        if (handleDealerSelection && !isReduxFormIntegrated) {
            handleDealerSelection(selectedOption);
        }

        // Update parent component with dealerNumber and dealerName
        if (onDealerChange) {
            if (selectedOption) {
                onDealerChange({
                    dealerNumber: selectedOption.value,
                    dealerName: selectedOption.dealerName || ''
                });
            } else {
                onDealerChange({
                    dealerNumber: '',
                    dealerName: ''
                });
            }
        }
    };

    const getDefaultValue = () => {
        if (dealerNumber) {
            return {
                value: dealerNumber,
                label: `${dealerNumber} / ${dealerName || ''}`
            };
        }
        return null;
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 31,
            height: 31,
            backgroundColor: state.isDisabled ? '#e9ecef' : base.backgroundColor,
            borderColor: hasError ? '#dc3545' : (state.isDisabled ? '#ced4da' : base.borderColor),
            borderWidth: hasError ? '2px' : base.borderWidth,
            '&:hover': {
                borderColor: hasError ? '#dc3545' : base.borderColor
            }
        }),
        valueContainer: (base) => ({
            ...base,
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 8,
            paddingRight: 8,
            fontSize: '0.95rem',
            height: 31,
            display: 'flex',
            alignItems: 'center',
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: 31,
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
            fontSize: '0.95rem'
        }),
        singleValue: (base, state) => ({
            ...base,
            color: state.isDisabled ? '#6c757d' : base.color,
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            height: 31,
            paddingBottom: 2,
        }),
        placeholder: (base) => ({
            ...base,
            fontSize: '0.95rem',
            color: '#6c757d',
            display: 'flex',
            alignItems: 'center',
            height: 31,
            paddingBottom: 2,
        })
    };

    return (
        <div>
            {label && (
                <div className="row m-0">
                    <label className="col-form-label pl-0 font-weight-bold">
                        <FormattedMessage {...label}/>
                        {isRequired && <span style={{color: 'red', marginLeft: '4px'}}>*</span>}
                    </label>
                </div>
            )}
            <AsyncSelect 
                placeholder={placeholder ? formatMessage(placeholder) : formatMessage(exportMessages.DEALER_SEARCH_PLACEHOLDER)}
                noOptionsMessage={() => noOptionsMessage ? formatMessage(noOptionsMessage) : formatMessage(exportMessages.DEALER_SEARCH_NO_OPTIONS)}
                loadOptions={debounce(DEFAULT_DEBOUNCE_TIMEOUT, handleDealerSearchInternal)}
                value={getDefaultValue()}
                onChange={handleSelectionChange}
                isDisabled={isDisabled}
                styles={selectStyles}
            />
            {hasError && (
                <ErrorDiv className="invalid-feedback">
                    <ValidFormattedMessage message={meta.error} intlMessages={validationMessages}/>
                </ErrorDiv>
            )}
        </div>
    );
};

SelectDealer.propTypes = {
    label: PropTypes.object,
    placeholder: PropTypes.object,
    noOptionsMessage: PropTypes.object,
    handleDealerSelection: PropTypes.func,
    handleDealerSearch: PropTypes.func,
    handleDealerSearchCallback: PropTypes.func,
    intl: PropTypes.object.isRequired,
    dealerNumber: PropTypes.string,
    dealerName: PropTypes.string,
    onDealerChange: PropTypes.func,
    searchAseDealers: PropTypes.func,
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    input: PropTypes.object,
    meta: PropTypes.object
};

SelectDealer.defaultProps = {
    isRequired: false,
    isDisabled: false
};

export default injectIntl(SelectDealer);
